import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { useMangoApiClient } from '~/atoms/MangoApi'
import { useModalState } from '~/atoms/ModalState'
import { useUserState } from '~/atoms/UserState'
import { toaster } from '~/components/ui/toaster'
import { GeneralOperation } from '~/hooks/toast/eventHandlers/generalToastEventHandler'
import { useEmitterService } from '~/hooks/useEmitterService'
import { usePhoneEndpoint } from '~/hooks/usePhone'
import {
  NotificationDisposition,
  NotificationDomain,
  NotificationEmitterEventType,
} from '~/services/EventEmitter/types/NotificationEmitterTypes'

import { Pbx } from '@mangovoice/types'

const PBX_QUERY_PARAM_KEY = 'pbx_id'

const parsePbxId = (rawPbxId: string | null): number | null => {
  if (!rawPbxId || !/^\d+$/.test(rawPbxId)) {
    return null
  }

  const parsedPbxId = Number(rawPbxId)
  if (!Number.isSafeInteger(parsedPbxId) || parsedPbxId <= 0) {
    return null
  }

  return parsedPbxId
}

export const PbxQueryParamPbxSwitchListener = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const api = useMangoApiClient()
  const { pbx, getUserSessionDependencies, isErrored } = useUserState({
    canAutoRefresh: false,
  })
  const { emit } = useEmitterService()
  const {
    state: {
      phoneCalls: { list },
    },
  } = usePhoneEndpoint({})
  const {
    confirmModal: [, setConfirmModalState],
    callInProgressModal: [, setCallInProgressModalState],
  } = useModalState()
  const [expectedPbxId, setExpectedPbxId] = useState<number | undefined>()
  const latestRequestIdRef = useRef(0)

  const hasOpenCall = useMemo(() => list.callList.size > 0, [list.callList])

  useEffect(() => {
    if (!expectedPbxId || expectedPbxId !== pbx?.id) {
      return
    }

    emit(NotificationEmitterEventType.PROCESS_UPDATED, {
      disposition: NotificationDisposition.Success,
      domain: NotificationDomain.GENERAL,
      operation: GeneralOperation.SwitchedPbx,
      message: pbx?.name,
    })
    setExpectedPbxId(undefined)
  }, [emit, expectedPbxId, pbx?.id, pbx?.name])

  useEffect(() => {
    if (expectedPbxId && isErrored) {
      setExpectedPbxId(undefined)
    }
  }, [expectedPbxId, isErrored])

  useEffect(() => {
    if (!pbx?.id || !searchParams.has(PBX_QUERY_PARAM_KEY)) {
      return
    }

    const rawPbxId = searchParams.get(PBX_QUERY_PARAM_KEY)
    const requestedPbxId = parsePbxId(rawPbxId)

    const nextSearchParams = new URLSearchParams(searchParams)
    nextSearchParams.delete(PBX_QUERY_PARAM_KEY)
    setSearchParams(nextSearchParams, { replace: true })

    const showInvalidPbxWarning = () => {
      toaster.create({
        title: t('pbxQuerySwitch.warning.invalid.title', 'Invalid PBX Link'),
        description: t(
          'pbxQuerySwitch.warning.invalid.description',
          'The PBX link is invalid. Please try again from Admin.',
        ),
        type: 'warning',
        duration: 5000,
        closable: true,
      })
    }

    const showInaccessiblePbxWarning = () => {
      toaster.create({
        title: t(
          'pbxQuerySwitch.warning.inaccessible.title',
          'Unable to Switch PBX',
        ),
        description: t(
          'pbxQuerySwitch.warning.inaccessible.description',
          "You don't have access to that PBX.",
        ),
        type: 'warning',
        duration: 5000,
        closable: true,
      })
    }

    if (!requestedPbxId) {
      showInvalidPbxWarning()
      return
    }

    if (requestedPbxId === pbx.id) {
      return
    }

    if (hasOpenCall) {
      setCallInProgressModalState({ open: true })
      return
    }

    const requestId = ++latestRequestIdRef.current

    void api.pbxes
      .getPbx(requestedPbxId)
      .then(({ ok, data }) => {
        if (requestId !== latestRequestIdRef.current) {
          return
        }

        if (!ok || !data) {
          showInaccessiblePbxWarning()
          return
        }

        const requestedPbx = data as Pbx
        setConfirmModalState({
          title: t('pbxQuerySwitch.confirm.title', 'Set your PBX?'),
          message: t(
            'pbxQuerySwitch.confirm.message',
            'Want to set your PBX to {{pbxName}}?',
            {
              pbxName: requestedPbx.name,
            },
          ),
          confirmText: t('pbxQuerySwitch.confirm.confirmText', 'Set PBX'),
          cancelText: t('pbxQuerySwitch.confirm.cancelText', 'Cancel'),
          closeAfterConfirm: true,
          isDestructive: false,
          onConfirm: () => {
            setExpectedPbxId(requestedPbx.id)
            getUserSessionDependencies(requestedPbx)
          },
        })
      })
      .catch(() => {
        if (requestId !== latestRequestIdRef.current) {
          return
        }

        showInaccessiblePbxWarning()
      })
  }, [
    api,
    getUserSessionDependencies,
    hasOpenCall,
    pbx?.id,
    searchParams,
    setCallInProgressModalState,
    setConfirmModalState,
    setSearchParams,
    t,
  ])

  return null
}
